/**
 * Privacy-respecting analytics facade.
 *
 * Events are buffered locally and flushed to the API only when the player has
 * granted analytics consent (SOC 2 / privacy-by-default). No PII is ever
 * attached; identifiers are random per-install and can be rotated by the user.
 */
import { loadState, saveState } from './storage';

export type AnalyticsEvent =
  | 'app_open'
  | 'page_view'
  | 'game_open'
  | 'game_spin'
  | 'bet_placed'
  | 'win'
  | 'signup_start'
  | 'signup_complete'
  | 'login'
  | 'logout'
  | 'deposit_simulated'
  | 'responsible_limits_set';

interface QueuedEvent {
  name: AnalyticsEvent;
  props: Record<string, string | number | boolean>;
  ts: number;
}

const CONSENT_KEY = 'analytics-consent';
const ANON_KEY = 'analytics-anon-id';
const FLUSH_THRESHOLD = 20;

let consent = loadState<boolean>(CONSENT_KEY, false);
let queue: QueuedEvent[] = [];

function anonId(): string {
  let id = loadState<string | null>(ANON_KEY, null);
  if (!id) {
    id = `anon_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    saveState(ANON_KEY, id);
  }
  return id;
}

export function setAnalyticsConsent(value: boolean): void {
  consent = value;
  saveState(CONSENT_KEY, value);
  if (!value) queue = [];
}

export function hasAnalyticsConsent(): boolean {
  return consent;
}

export function track(
  name: AnalyticsEvent,
  props: Record<string, string | number | boolean> = {},
): void {
  if (!consent) return;
  queue.push({ name, props, ts: Date.now() });
  if (queue.length >= FLUSH_THRESHOLD) void flush();
}

export async function flush(): Promise<void> {
  if (!consent || queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    const base = import.meta.env.VITE_API_BASE_URL;
    if (!base) return;
    await fetch(`${base}/telemetry`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ anonId: anonId(), events: batch }),
      keepalive: true,
    });
  } catch {
    // Telemetry must never break gameplay. Re-queue a bounded amount.
    queue = [...batch.slice(-FLUSH_THRESHOLD), ...queue].slice(-FLUSH_THRESHOLD * 2);
  }
}
